import React, { useEffect } from 'react';
import { cn } from '../../lib/utils';
import { useUIStore, type Toast } from '../../lib/uiStore';
import { CheckCircle, XCircle, AlertTriangle, Info, X, Undo2 } from 'lucide-react';

const icons: Record<string, React.ReactNode> = {
  success: <CheckCircle className="h-5 w-5 text-success-500" />,
  error: <XCircle className="h-5 w-5 text-danger-500" />,
  warning: <AlertTriangle className="h-5 w-5 text-warning-500" />,
  info: <Info className="h-5 w-5 text-info-500" />,
};

const borderColors: Record<string, string> = {
  success: 'border-l-success-500',
  error: 'border-l-danger-500',
  warning: 'border-l-warning-500',
  info: 'border-l-info-500',
};

export function ToastContainer() {
  const toasts = useUIStore((s) => s.toasts);
  const removeToast = useUIStore((s) => s.removeToast);

  return (
    <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-3 max-w-sm">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={() => removeToast(t.id)} />
      ))}
    </div>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onDismiss, toast.duration || 4000);
    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onDismiss]);

  return (
    <div
      className={cn(
        'bg-surface-raised/88 backdrop-blur-2xl border border-border border-l-4 rounded-[24px] shadow-elevated px-4 py-3',
        'animate-slide-up flex items-start gap-3 min-w-[300px]',
        borderColors[toast.variant]
      )}
      role="alert"
    >
      <div className="flex-shrink-0 mt-0.5">{icons[toast.variant]}</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold tracking-[-0.02em] text-content">{toast.title}</p>
        {toast.description && <p className="text-xs text-content-secondary mt-0.5">{toast.description}</p>}
        {toast.action && (
          <button
            onClick={() => { toast.action!.onClick(); onDismiss(); }}
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-primary-500 hover:text-primary-400 transition-colors"
          >
            <Undo2 className="h-3 w-3" />
            {toast.action.label}
          </button>
        )}
      </div>
      <button onClick={onDismiss} className="text-content-muted hover:text-content transition-colors flex-shrink-0 rounded-full p-1 hover:bg-surface-overlay">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
